import React, { useEffect, useRef, useState } from 'react';
import { fmtDateLong } from '../../utils/cameraRelay';
import { cameraProblem, healthFor } from './CameraTile';
import { C } from './CamerasStyles';

/**
 * One line for the whole camera system ("9 of 9 live · all recording |
 * 2.6 of 3.7 TB · 126 days kept"); click it for the per-camera detail.
 */
export function summarize(cameras, health, healthError) {
  if (healthError) return { tone: 'bad', main: 'Camera system unreachable', side: '' };
  if (!health || !cameras) return { tone: 'muted', main: 'Checking cameras…', side: '' };
  const total = cameras.length;
  let live = 0;
  let recording = 0;
  for (const cam of cameras) {
    const h = healthFor(health, cam);
    if (h && h.stream === 'ok') live += 1;
    if (h && h.recording === 'ok') recording += 1;
  }
  const tunnelBad = health.tunnel && !['ok', 'unknown'].includes(health.tunnel.status);
  const parts = [`${live} of ${total} live`];
  parts.push(recording === total ? 'all recording' : `${recording} of ${total} recording`);
  if (tunnelBad) parts.push('tunnel down');
  const tone = live === total && recording === total && !tunnelBad ? 'ok' : 'warn';
  return { tone, main: parts.join(' · ') };
}

export const STATUS_TONE = { ok: C.live, warn: C.warn, bad: C.bad, muted: '#5f6771' };
const TONE = STATUS_TONE;

export default function StatusPill({ cameras, health, healthError, retention }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const s = summarize(cameras, health, healthError);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const disk = health?.disk;
  const diskText = disk && disk.total_gb > 0
    ? `${(disk.used_gb / 1024).toFixed(1)} of ${(disk.total_gb / 1024).toFixed(1)} TB`
    : null;
  const keptText = retention?.days ? `${retention.days} days kept` : null;
  const side = [diskText, keptText].filter(Boolean).join(' · ');

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" className="jqc-btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="jqc-dot" style={{ background: TONE[s.tone] }} />
        <span style={{ color: s.tone === 'warn' ? C.warn : s.tone === 'bad' ? C.bad : C.text }}>{s.main}</span>
        {side && <span style={{ color: '#4a5159' }}>|</span>}
        {side && <span style={{ color: C.muted, fontWeight: 400 }}>{side}</span>}
      </button>
      {open && (
        <div className="jqc-pop" role="dialog" aria-label="Camera system status">
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Camera system</div>
          {healthError && <div style={{ color: C.bad, fontSize: 13, marginBottom: 8 }}>The relay at cameras.kancraftmfg.com isn't answering.</div>}
          {(cameras || []).map((cam) => {
            const h = healthFor(health, cam);
            const problem = cameraProblem(h);
            return (
              <div key={cam.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
                <span className="jqc-dot" style={{ background: !h ? '#5f6771' : problem ? C.warn : C.live }} />
                <span style={{ flex: 1 }}>{cam.label}</span>
                <span style={{ color: problem ? C.warn : C.muted, fontSize: 12 }}>
                  {!h ? 'no data' : problem || `last clip ${Math.max(0, Math.round(h.last_file_age_sec))} s ago`}
                </span>
              </div>
            );
          })}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 4, fontSize: 12, color: C.muted, display: 'grid', gap: 3 }}>
            {diskText && <span>Storage: {diskText} used ({disk.used_pct}%)</span>}
            {retention?.oldest && <span>Oldest recording: {fmtDateLong(retention.oldest)} ({retention.days} days)</span>}
            {health?.tunnel && <span>Tunnel: {health.tunnel.status}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
